import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import {
  NewSubscriptionInitializer,
  NewSubscriptionVO
} from "@/types/REST-API-DOMAIN/NewSubscriptionVO";
import stripe from "stripe";
import { OrgAccess } from "@/types/REST-API-DOMAIN/OrgAccess";
import { SubscriptionCardData } from "@/types/SubscriptionCardData";
import {
  getPrice,
  getProductsAndPricesObject,
  getTeamsForExistingOrg
} from "@/helpers/api";
import { SubscriptionProductsVO } from "@/types/REST-API-DOMAIN/SubscriptionProductsVO";

@Module({ namespaced: true, name: "SubscriptionSignup" })
export default class SubscriptionSignup extends VuexModule {
  subscriptionVO: NewSubscriptionVO = new NewSubscriptionInitializer();
  licensePrice: stripe.Price | null = null;
  connectPrices: Array<stripe.Price> = [] as Array<stripe.Price>;
  dataPrices: Array<stripe.Price> = [] as Array<stripe.Price>;
  promotions: Array<stripe.PromotionCode> = [] as Array<stripe.PromotionCode>;

  showPromo = false;
  showPrivacy = false;
  showTerms = false;
  termsChecked = false;

  yearlySubscriptionData: SubscriptionCardData = {
    productTitle: "Annual License",
    billingFrequency: "Annually",
    billingFrequencyTitle: "Annual",
    icon: "check-circle-outline",
    monthlyPrice: 4.99,
    months: 12,
    product: "",
    price: ""
  };

  monthlySubscriptionData: SubscriptionCardData = {
    productTitle: "Monthly License",
    billingFrequency: "Monthly",
    billingFrequencyTitle: "Monthly",
    icon: "calendar-month",
    monthlyPrice: 6.99,
    months: 1,
    product: "",
    price: ""
  };

  selectedLicense: SubscriptionCardData = this.yearlySubscriptionData;
  get orgName() {
    return this.subscriptionVO.orgName;
  }
  get orgId() {
    return this.subscriptionVO.orgId;
  }
  get connectDiscount() {
    if (this.selectedConnectPrice) {
      const promotion: stripe.PromotionCode | null = this.activePromotion(
        this.selectedConnectPrice.productId
      );
      const amountOff = promotion
        ? this.amountOff(promotion, this.selectedConnectPrice.amount) || 0
        : 0;
      return (
        Math.round(10000 * (this.selectedConnectPrice.amount - amountOff)) /
        10000
      );
    } else {
      return null;
    }
  }

  get dataDiscount() {
    if (this.selectedDataPrice) {
      const promotion: stripe.PromotionCode | null = this.activePromotion(
        this.selectedDataPrice.productId
      );
      const amountOff = promotion
        ? this.amountOff(promotion, this.selectedDataPrice.amount) || 0
        : 0;
      return (
        Math.round(10000 * (this.selectedDataPrice.amount - amountOff)) / 10000
      );
    } else {
      return null;
    }
  }

  get proration() {
    const now = new Date();
    const prorationChargeDate = now.setDate(
      now.getDate() + this.subscriptionVO.trialDays
    );
    const trialMonth = now.setDate(1);
    const planAnchorDate = now.setMonth(now.getMonth() + 1);

    const prorationDays = Math.ceil(
      (planAnchorDate - prorationChargeDate) / (1000 * 3600 * 24)
    );
    const totalMonthDays = Math.ceil(
      (planAnchorDate - trialMonth) / (1000 * 3600 * 24)
    );

    const prorationPricePerTeam =
      Math.round(
        100 *
          ((this.selectedLicense.monthlyPrice / totalMonthDays) * prorationDays)
      ) / 100;

    const total = this.subscriptionVO.teams * prorationPricePerTeam;

    const product: any = this.licensePrice?.product;
    const promotion: stripe.PromotionCode | null = this.activePromotion(
      product
    );
    const amountOff = promotion ? this.amountOff(promotion, total) || 0 : 0;

    return {
      prorationChargeDate: prorationChargeDate,
      //Date the plan begins ( next 1st of the month, the remainder of the current month will be prorated)
      planAnchorDate: planAnchorDate,
      prorationDays: prorationDays,
      prorationPricePerTeam: prorationPricePerTeam,
      discountAmount: amountOff,
      subtotal: total,
      total: total - amountOff
    };
  }

  get licenseSubTotal() {
    return this.licensePrice
      ? // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
        (Math.round(100 * (this.licensePrice.unitAmountDecimal / 100)) / 100) *
          this.subscriptionVO.teams
      : 0;
  }

  get isLicenseDiscount() {
    const product: any = this.licensePrice?.product;
    return this.activePromotion(product);
  }

  get discountPercentageString() {
    // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
    return this.isLicenseDiscount?.coupon.percentOff
      ? // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
        `${this.isLicenseDiscount?.coupon.percentOff}%`
      : "";
  }

  get isDataDiscount() {
    return this.selectedDataPrice
      ? this.activePromotion(this.selectedDataPrice.productId)
      : null;
  }

  get isConnectDiscount() {
    return this.selectedConnectPrice
      ? this.activePromotion(this.selectedConnectPrice.productId)
      : null;
  }

  get licenseTotal() {
    const product: any = this.licensePrice?.product;
    const promotion: stripe.PromotionCode | null = this.activePromotion(
      product
    );

    const amountOff = promotion
      ? this.amountOff(promotion, this.licenseSubTotal) || 0
      : 0;

    return (Math.round(100 * (this.licenseSubTotal - amountOff)) / 100).toFixed(
      2
    );
  }

  get amountOff() {
    return (promotion: stripe.PromotionCode, price: number) => {
      // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
      if (promotion.coupon.amountOff) {
        // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
        return promotion.coupon.amountOff / 100;
      }
      // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
      if (promotion.coupon.percentOff) {
        return (
          // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
          Math.round((promotion.coupon.percentOff / 100) * price * 10000) /
          10000
        );
      }
    };
  }

  get activePromotion() {
    return (product: string) => {
      const promo: stripe.PromotionCode | null = this.subscriptionVO.promotion;
      if (promo && promo.active && promo.coupon.valid) {
        if (
          // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
          promo.coupon.appliesTo &&
          // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
          promo.coupon.appliesTo.products &&
          // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
          promo.coupon.appliesTo.products.length > 0
        ) {
          if (
            // @ts-ignore some Stripe properties expect underscores not camelcase. Camelcase is the correct var name
            promo.coupon.appliesTo.products.some((id: string) => id === product)
          ) {
            return promo;
          } else {
            return null;
          }
        } else {
          return promo;
        }
      }
      return null;
    };
  }

  get selectedDataPrice() {
    return this.formattedDataPrices.find(
      (data: any) => data.id === this.subscriptionVO.dataPriceId
    );
  }

  get selectedConnectPrice() {
    return this.formattedConnectPrices.find(
      (data: any) => data.id === this.subscriptionVO.connectPriceId
    );
  }

  get formattedConnectPrices() {
    return this.connectPrices.map((price: any) => {
      return {
        id: price.id,
        productId: price.product,
        unitAmountDecimal: price.unitAmountDecimal,
        amount: price.unitAmountDecimal / 100,
        price: `$${price.unitAmountDecimal / 100} / Particpant / Minute`
      };
    });
  }

  get formattedDataPrices() {
    return this.dataPrices.map((price: any) => {
      return {
        id: price.id,
        productId: price.product,
        unitAmountDecimal: price.unitAmountDecimal,
        amount: price.unitAmountDecimal / 100,
        price: `$${price.unitAmountDecimal / 100} / GB / Month`
      };
    });
  }

  @Mutation
  setConnectPrices(prices: Array<stripe.Price>) {
    this.connectPrices = prices;
  }

  @Mutation
  setDataPrices(prices: Array<stripe.Price>) {
    this.dataPrices = prices;
  }

  @Mutation
  setDataPrice(priceId: string) {
    this.subscriptionVO.dataPriceId = priceId;
  }

  @Mutation
  setConnectPrice(priceId: string) {
    this.subscriptionVO.connectPriceId = priceId;
  }

  @Mutation
  setLicensePrice(priceId: string) {
    this.subscriptionVO.priceId = priceId;
  }

  @Mutation
  setOrganization(org: OrgAccess) {
    this.subscriptionVO.orgId = org.orgId;
    this.subscriptionVO.orgName = org.orgName;
  }

  @Mutation
  setPromotions(promotions: Array<stripe.PromotionCode>) {
    this.promotions = promotions;
  }

  @Mutation
  setPromotion(promotion: stripe.PromotionCode) {
    this.subscriptionVO.promotion = promotion;
  }

  @Mutation
  clearPromotion() {
    this.subscriptionVO.promotion = null;
  }

  @Mutation
  setShowPromo(bool: boolean) {
    this.showPromo = bool;
  }

  @Mutation
  setShowTerms(bool: boolean) {
    this.showTerms = bool;
  }
  @Mutation
  setTermsChecked(bool: boolean) {
    this.termsChecked = bool;
  }

  @Mutation
  setShowPrivacy(bool: boolean) {
    this.showPrivacy = bool;
  }
  @Mutation
  setSelectedLicense(data: SubscriptionCardData) {
    this.selectedLicense = data;
  }
  @Mutation
  setLicensePriceObject(price: stripe.Price) {
    this.licensePrice = price;
  }

  @Mutation
  setTeams(count: number) {
    this.subscriptionVO.teams = count;
  }

  @Mutation
  setProductsAndPrices(data: SubscriptionProductsVO) {
    this.yearlySubscriptionData.product = data.yearlyProductId;
    this.yearlySubscriptionData.price = data.yearlyPriceId;

    this.yearlySubscriptionData.monthlyPrice =
      // @ts-ignore
      data.yearlyPrice.unitAmountDecimal / 100 / 12;

    this.monthlySubscriptionData.product = data.monthlyProductId;
    this.monthlySubscriptionData.price = data.monthlyPriceId;

    this.monthlySubscriptionData.monthlyPrice =
      // @ts-ignore
      data.monthlyPrice.unitAmountDecimal / 100;

    this.subscriptionVO.priceId = data.yearlyPriceId;
    this.subscriptionVO.connectPriceId = data.connectDefaultPriceId;
    this.subscriptionVO.dataPriceId = data.dataDefaultPriceId;
  }

  @Action
  async getLicensePrice(data: SubscriptionCardData) {
    const price = await getPrice(data.price);
    this.context.commit("setSelectedLicense", data);
    this.context.commit("setLicensePrice", data.price);
    this.context.commit("setLicensePriceObject", price.data);
  }

  @Action
  async setExistingOrg(data: OrgAccess) {
    if (data.orgId) {
      const teams = await getTeamsForExistingOrg(data.orgId);
      this.context.commit("setOrganization", data);
      this.context.commit("setTeams", teams.data);
    }
  }

  @Action
  async getProductsAndPrices() {
    const data = await getProductsAndPricesObject();
    this.context.commit("setProductsAndPrices", data);
  }
}
